// @file Vue composable for all autoplay related logic in Slideshow
import { getSearchParam, isSearchParamTruthy } from '@@/bits/location'
import type { AutoplayDurationType } from '@@/bits/slideshow'
import { AUTOPLAY_DURATIONS, calculateAutoSlideDuration } from '@@/bits/slideshow'
import { useAutoplayWithIndicatorAnimation } from '@@/vuecomposables/autoplay'
import { useSlideshowAttachments } from '@@/vuecomposables/slideshow_attachments'
import { useSlideshowOverlays } from '@@/vuecomposables/slideshow_overlays'
import { useSlideshowPages } from '@@/vuecomposables/slideshow_pages'
import { onMounted, watch } from 'vue'

export const useSlideshowAutoplay = (): {
  isAutoplaying: typeof isAutoplaying
  isAutoplayPaused: typeof isAutoplayPaused
  isAutoplayLooping: typeof isAutoplayLooping
  selectedAutoplayDuration: typeof selectedAutoplayDuration
  computedAutoplayDurationInMs: typeof computedAutoplayDurationInMs
  indicatorTransformScaleX: typeof indicatorTransformScaleX
  toggleAutoplay: typeof toggleAutoplay
  onAutoplayLoopingToggle: typeof onAutoplayLoopingToggle
  onAutoplayDurationSelected: typeof onAutoplayDurationSelected
} => {
  const { onNext, onRestart, isLastPage, currentPageIndex } = useSlideshowPages()
  const { areAttachmentsLoadedForCurrentPost, numberOfWordsOnPage, numberOfAttachmentsOnPage } =
    useSlideshowAttachments()
  const { isAnyOverlayOpen } = useSlideshowOverlays()
  const {
    autoplayTimer,
    isAutoplaying,
    isAutoplayPaused,
    isAutoplayLooping,
    selectedAutoplayDuration,
    computedAutoplayDurationInMs,
    indicatorTransformScaleX,
    startAutoplay,
    onAutoplayDurationSelected,
    onAutoplayLoopingToggle,
    toggleAutoplay,
  } = useAutoplayWithIndicatorAnimation({
    currentPageIndex,
    isLastPage,
    autoplayButtonId: 'slideshow-autoplay-button',
    isAnyOtherOverlayOpen: isAnyOverlayOpen,
    computeAutoplayDurationInMs: (x: AutoplayDurationType) => {
      if (selectedAutoplayDuration.value === 'auto') {
        return calculateAutoSlideDuration(numberOfWordsOnPage.value, numberOfAttachmentsOnPage.value)
      }
      return selectedAutoplayDuration.value * 1000
    },
    onNext,
    onRestart,
  })

  // At `SurfaceSlideshowContent.vue`, we have a OVERSCAN_PAGE_LIMIT for preloading slides in the background
  // so usually post attachments would be loaded by the time user reaches the current slide.
  // But in the case it has not finished loading, we pause autoplaying and resume when it has finished loading
  watch(areAttachmentsLoadedForCurrentPost, () => {
    if (!areAttachmentsLoadedForCurrentPost.value) {
      autoplayTimer.stop()
    } else if (isAutoplaying.value) {
      startAutoplay()
    }
  })
  // #region init slideshow autoplay from query params
  const initSlideshowAutoplayFromQueryParams = (): void => {
    const loopParam = getSearchParam('loop')
    if (isSearchParamTruthy(loopParam)) {
      isAutoplayLooping.value = true
    }

    const durationParam = getSearchParam('duration')
    if (durationParam != null && durationParam in AUTOPLAY_DURATIONS) {
      onAutoplayDurationSelected(durationParam as AutoplayDurationType)
    }

    const autoplayParam = getSearchParam('autoplay')
    if (isSearchParamTruthy(autoplayParam)) {
      startAutoplay()
    }
  }

  onMounted(() => {
    initSlideshowAutoplayFromQueryParams()
  })
  // #endregion

  return {
    isAutoplaying,
    isAutoplayPaused,
    isAutoplayLooping,
    selectedAutoplayDuration,
    computedAutoplayDurationInMs,
    indicatorTransformScaleX,

    toggleAutoplay,
    onAutoplayLoopingToggle,
    onAutoplayDurationSelected,
  }
}
